import type { LogContext, Logger, LoggerConfig } from './types'

/**
 * Creates a new {@link Logger} that merges `baseContext` into every log call.
 * Per-call context overrides base context keys. Keeps `base`'s config, so a
 * logger that requires `evt.name` still does.
 *
 * @example
 * ```ts
 * const billingLogger = withContext(appLogger, { module: 'billing' })
 * billingLogger.info('Invoice created', { invoiceId: '123' })
 * // → appLogger.info('Invoice created', { module: 'billing', invoiceId: '123' })
 * ```
 */
export function withContext<C extends LoggerConfig>(
  base: Logger<C>,
  baseContext: LogContext,
): Logger<C> {
  return {
    debug: (msg, ctx) => base.debug(msg, { ...baseContext, ...ctx }),
    info: (msg, ctx) => base.info(msg, { ...baseContext, ...ctx }),
    warn: (msg, details) => base.warn(msg, { ...baseContext, ...details }),
    error: (msg, details) => base.error(msg, { ...baseContext, ...details }),
  }
}
